import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Link, useNavigate } from 'react-router-dom';
import './EmployeeEvent.css';
import './BcpMap.css';

const formatDate = (dateString) => {
  if (!dateString) return '未設定';
  const date = new Date(dateString);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}/${m}/${d}`;
};

const hhmm = (iso) => {
  if (!iso) return '';
  const t = iso.includes('T') ? iso.split('T')[1] : iso;
  return t.substring(0, 5);
};

const StatusBadge = ({ status, isCancelled }) => {
  if (isCancelled) {
    return <span className="status-badge status-cancelled status-cancelled-lg">🚫 中止</span>;
  }
  if (status === 'yes')
    return <span className="status-badge status-yes">✅ 参加予定</span>;
  if (status === 'no')
    return <span className="status-badge status-no">❌ 不参加</span>;
  return <span className="status-badge status-pending">⏳ 未回答</span>;
};

const EmployeeEvents = () => {
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchEvents = () => {
    setError(null);
    setLoading(true);
    api.get(`/events/upcoming/?_t=${Date.now()}`)
      .then((res) => { setUpcomingEvents(res.data); setError(null); })
      .catch((err) => { console.error('イベント取得エラー:', err); setError('イベントを取得できませんでした。'); setUpcomingEvents([]); })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchEvents();
    const onVisible = () => { if (document.visibilityState === 'visible') fetchEvents(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  if (loading) return <div className="loading-text">イベントを読み込んでいます...</div>;

  if (error) {
    return (
      <div className="bcp-page event-employee-container">
        <h2 className="page-title">イベント一覧</h2>
        <p className="en-error">{error}</p>
        <div className="bcp-btn-group">
          <button type="button" className="bcp-btn-back" onClick={fetchEvents}>再読み込み</button>
          <button type="button" className="bcp-btn-back" onClick={() => navigate('/card_back')}>戻る</button>
        </div>
      </div>
    );
  }

  if (upcomingEvents.length === 0)
    return (
      <div className="bcp-page event-employee-container">
        <h2 className="page-title">イベント一覧</h2>
        <p className="no-event-text">現在、開催予定のイベントはありません。</p>
        <div className="bcp-btn-group">
          <button type="button" className="bcp-btn-back" onClick={() => navigate('/card_back')}>戻る</button>
        </div>
      </div>
    );

  return (
    <div className="bcp-page event-employee-container">
      <h2 className="page-title">イベント一覧</h2>
      <ul className="event-list">
        {upcomingEvents.map((event) => {
          // 参加可否が未回答（yes/no以外）かどうか
          const isUnanswered = event.userStatus !== 'yes' && event.userStatus !== 'no';
          // 回答締切（画面表示と同じく regDeadline を基準にする）
          const deadlineStr = event.regDeadline ? formatDate(event.regDeadline) : null;
          const todayStr = formatDate(new Date());
          // 締切日に達した（当日以降）／まだ回答可能（締切日当日まで）
          const deadlineReached = deadlineStr && todayStr >= deadlineStr;
          const answerOpen = deadlineStr ? todayStr <= deadlineStr : true;
          // 未回答かつ締切日到達 → リマインド表示
          const showReminder = !event.is_cancelled && isUnanswered && deadlineReached;
          return (
          <li
            key={event.id}
            className={`event-list-item${event.is_cancelled ? ' event-list-item-cancelled' : ''}`}
            onClick={() => navigate(`/event/${event.id}`)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && navigate(`/event/${event.id}`)}
          >
            <div className="event-item-body">
              <div className="event-title-row">
                <h5 className="event-name">{event.eventName}</h5>
                {!event.is_cancelled && (
                  <StatusBadge status={event.userStatus} isCancelled={false} />
                )}
              </div>

              {event.is_cancelled ? (
                <div className="event-cancelled-panel">
                  <StatusBadge isCancelled={true} />
                  <p className="event-cancelled-panel-reason">
                    {event.cancel_reason ? (
                      <>
                        <span className="event-cancelled-panel-label">中止理由</span>
                        {event.cancel_reason}
                      </>
                    ) : (
                      <span className="event-cancelled-panel-empty">中止理由は登録されていません</span>
                    )}
                  </p>
                </div>
              ) : (
                <>
                  <div className="event-meta">
                    <span>📅 {formatDate(event.eventDate)}</span>
                    <span>🕐 {hhmm(event.startTime)} 〜 {hhmm(event.endTime)}</span>
                    {event.location && <span>📍 {event.location}</span>}
                  </div>

                  {event.description && (
                    <p className="event-description">
                      {event.description.length > 60
                        ? event.description.substring(0, 60) + '…'
                        : event.description}
                    </p>
                  )}

                  {event.regDeadline && (
                    <p className="event-deadline">
                      ⏰ 回答締切: {formatDate(event.regDeadline)}
                    </p>
                  )}
                  {/*}
                  {showReminder && (
                    <div className="event-reminder">
                      <span className="event-reminder-text">
                        ⚠️ 回答締切になりました。参加可否が未回答です。
                        {!answerOpen && '（締切が過ぎています）'}
                      </span>
                      <Link
                        to={`/event/${event.id}`}
                        className="event-reminder-btn"
                        onClick={(e) => e.stopPropagation()}
                      >
                        回答する
                      </Link>
                    </div>
                  )}
                  */}
                  {event.hasAnketo === true && !event.isAnketoAnswered && (
                    <div className="anketo-badge">
                      <Link
                        to={`/event/${event.id}/anketo`}
                        className="anketo-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        📝 アンケートに回答する
                      </Link>
                    </div>
                  )}
                  {event.hasAnketo === true && event.isAnketoAnswered && (
                    <div className="anketo-badge-completed" style={{ color: '#888', fontSize: '0.9rem', marginTop: '8px' }}>
                      ✅ アンケート回答済み (ご協力ありがとうございました)
                    </div>
                  )}
                </>
              )}
            </div>

            {!event.is_cancelled && (
              <Link to={`/event/${event.id}`} className="btn-detail" onClick={(e) => e.stopPropagation()}>
                詳細
              </Link>
            )}
          </li>
          );
        })}
      </ul>
      <div className="btn-group">
        <button type="button" className="btn-back" onClick={() => navigate('/card_back')}>戻る</button>
      </div>
    </div>
  );
};

export default EmployeeEvents;
